import { Combobox } from '@headlessui/react';
import cx from 'classnames';
import { useRouter } from 'next/router';
import { ComponentProps, FormEvent, useEffect, useId, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import MapPin from '../../../images/icons/map-pin.svg';
import { logUIEvent } from '../../../lib/analytics/analytics';
import {
  Cloud$Interest,
  Cloud$SuggestedAddress,
} from '../../../types/cloudApi';
import getFirstFromArray from '../../../utils/getFirstFromArray';

import messages from '../../../../../breeze/components/Interest/messages';
import { useGlobalConfig } from '../../../hooks/configContext';
import ArrowRightSvg from '../../../images/icons/arrow-right.svg';
import { logToSentry } from '../../../lib/sentry';
import { Cloud$MainHardwareType } from '../../../types/cloudEnums';
import { Otovo$Locale } from '../../../types/otovoweb';
import Spinner from '../../LoadingScreens/Spinner';
import AddressSearchForm, { SubmissionMethod } from './AddressSearchForm';
import { validateOnServer } from './AddressValidation/addressValidationUtils';
import { useAddressValidation } from './AddressValidation/useAddressValidation';
import ErrorMessages from './ErrorHandling/ErrorMessages';
import StyledAddressInput from './StyledAddressInput';
import StyledAddressSuggestions from './StyledAddressSuggestions';
import { useAddressSuggestions } from './useAddressSuggestions';
import useCreateInterest from './useCreateInterest';

type Props = {
  onInterestCreated: (interest: Cloud$Interest) => void;
  onAddressesShown?: (state: 'open' | 'closed') => void;
  submissionMethod: SubmissionMethod;
  locale?: Otovo$Locale;
  mainHardwareType?: Cloud$MainHardwareType;
  version?: ComponentProps<typeof StyledAddressInput>['version'];
};

const AddressSearchBar = ({
  onInterestCreated,
  onAddressesShown,
  submissionMethod,
  locale,
  mainHardwareType,
  version = 'legacy',
}: Props) => {
  const intl = useIntl();
  const config = useGlobalConfig();
  const router = useRouter();
  const [inputRef, isInViewPort] = useIntersectionObserver<HTMLInputElement>({
    fireOnce: true,
  });
  const [submitError, setSubmitError] = useState<string>('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const id = useId();

  function resetSpinner() {
    setIsRedirecting(false);
  }

  useEffect(() => {
    router.events.on('routeChangeComplete', resetSpinner);
    return () => {
      router.events.off('routeChangeComplete', resetSpinner);
    };
  }, [router]);

  /**
   * Custom hooks
   * */
  const { createInterest, isCreatingInterest } = useCreateInterest();

  const {
    address,
    setAddress,
    suggestions,
    isLoadingSuggestions,
    suggestError,
  } = useAddressSuggestions(locale, mainHardwareType);

  const { state: validationState, actions: validationActions } =
    useAddressValidation();

  /**
   * Derived fields
   * */
  const error = suggestError || submitError || validationState.errorMessage;
  const isLoading =
    isLoadingSuggestions ||
    isCreatingInterest ||
    isRedirecting ||
    validationState.status === 'pending';

  const handleOnChange = (newQuery: string) => {
    // reset any errors on key change
    setSubmitError('');
    validationActions.resetError();

    logUIEvent(
      {
        action: 'User started typing an address',
      },
      { once: true, broadcastToParent: true },
    );

    setAddress({
      text: newQuery,
      id: '',
    });
  };

  useEffect(() => {
    if (window.addressInput) {
      handleOnChange(window.addressInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const routerQuery = router.query;

  useEffect(() => {
    const address = getFirstFromArray(routerQuery.address || '');
    if (!address) return;

    validateOnServer(
      { id: '', text: address },
      config.BU_CONFIG.slug,
      config.BU_CONFIG.market,
    )
      .then((validAddress) => {
        if (validAddress.id) {
          setAddress(validAddress);
        }
      })
      .catch((error) => {
        logToSentry('Unable to validate address from query parameter', {
          extra: { market: config.BU_CONFIG.market, address, error },
        });
      });
  }, [config.BU_CONFIG.market, config.BU_CONFIG.slug, routerQuery, setAddress]);

  /**
   * Effects
   * */
  useEffect(() => {
    if (isInViewPort && inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef, isInViewPort]);

  /**
   * Event handlers
   * */
  const handleError = (errorMessage: string) => {
    setSubmitError(errorMessage);
  };

  const handleOnSuccess = (interest?: Cloud$Interest) => {
    setIsRedirecting(true);

    if (interest) {
      onInterestCreated(interest);
    }
  };

  const handleSelectAddress = async (
    selectedAddress: Cloud$SuggestedAddress | null,
  ) => {
    if (!selectedAddress) {
      return;
    }

    setAddress(selectedAddress);

    if (submissionMethod === 'nativeForm') {
      // When using the nativeForm type, we need validate the address before
      // creating the interest.
      await validationActions.validate(selectedAddress, suggestions, 'server');
    }
  };

  const isBatterySalesFlow = mainHardwareType === 'battery_system';
  const placeholderText = isBatterySalesFlow
    ? intl.formatMessage(messages.inexactAddressLabel)
    : intl.formatMessage(messages.addressLabel);

  const addressBarId = `address-autocomplete-${id}`;
  return (
    <div className={version === 'legacy' ? 'relative h-10' : 'relative'}>
      <AddressSearchForm
        version={version}
        address={address}
        createInterest={createInterest}
        onError={handleError}
        onSuccess={handleOnSuccess}
        submissionMethod={submissionMethod}
        setAddress={setAddress}
        suggestions={suggestions}
        validateAddress={validationActions.validate}
        mainHardwareType={mainHardwareType}
      >
        <Combobox value={address} onChange={handleSelectAddress}>
          {({ open }) => {
            if (onAddressesShown) {
              onAddressesShown(open ? 'open' : 'closed');
            }
            return (
              <>
                <div className="color-scheme-white relative flex h-full w-full items-center">
                  {version !== 'v2025' && (
                    <div className="absolute left-[14px] hidden sm:block">
                      <MapPin
                        className="h-7 w-7"
                        color={config.theme.alias.border_100 || 'currentColor'}
                      />
                    </div>
                  )}

                  <label htmlFor={addressBarId} className="sr-only">
                    {placeholderText}
                  </label>

                  <StyledAddressInput
                    version={version}
                    type="search"
                    id={addressBarId}
                    name="address-autocomplete"
                    ref={inputRef}
                    displayValue={(selectedAddress: Cloud$SuggestedAddress) =>
                      selectedAddress.text
                    }
                    placeholder={placeholderText}
                    onChange={(event: FormEvent<HTMLInputElement>) => {
                      const newQuery = event.currentTarget.value;
                      handleOnChange(newQuery);
                    }}
                    open={open && !!suggestions?.length}
                  />
                  <script
                    type="text/javascript"
                    dangerouslySetInnerHTML={{
                      __html: `
                      document.querySelectorAll('[name="address-autocomplete"]').forEach(function (elem) {
                        elem.addEventListener('keyup', function (evt) {
                          window.addressInput = evt.currentTarget.value;
                        })
                      });
                    `,
                    }}
                  />

                  {isLoading && (
                    <div aria-live="polite" className="sr-only">
                      {intl.formatMessage(messages.loadingMessage)}
                    </div>
                  )}

                  <button
                    aria-label={intl.formatMessage(messages.submitButtonLabel)}
                    type="submit"
                    className={cx(
                      'absolute right-3 flex items-center justify-center rounded-full font-medium outline-hidden focus:ring-1 disabled:bg-grey-10 disabled:text-muted',
                      version === 'legacy'
                        ? 'bg-blue-50 p-3.5 text-lg text-white hover:bg-blue-60 focus:ring-blue-90'
                        : 'bg-peach-50 p-3 text-grey-90 ring-offset-2 hover:bg-peach-60 focus:ring-2 focus:ring-digital',
                      isLoading && 'cursor-not-allowed',
                    )}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Spinner size="w-5" color="text-white" />
                    ) : (
                      <ArrowRightSvg data-testid="arrowRightSvg" />
                    )}
                  </button>
                </div>
                {suggestions && !error && (
                  <StyledAddressSuggestions
                    suggestions={suggestions}
                    query={address.text || ''}
                  />
                )}
                <ErrorMessages error={error} />
              </>
            );
          }}
        </Combobox>
      </AddressSearchForm>
    </div>
  );
};

export default AddressSearchBar;
